import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { isSameDay } from 'date-fns';
import Button from '../buttons/Button';
import { CreateBankTransactionModel } from '../../../types/bankAccount';
import { Event, EventParticipationAnswer } from '../../../types/event';
import { formatEuroCents } from '../../../utils/bankAccountUtil';
import eventService from '../../../services/community/eventService';

type EventExpenseMatch = {
  transaction: CreateBankTransactionModel;
  matchedEvent?: Event;
  confirmed: boolean;
};

type VerifyEventExpenseMatchesProps = {
  unmatched: CreateBankTransactionModel[];
  onConfirm: (
    confirmed: {
      transaction: CreateBankTransactionModel;
      matchedEvent: Event;
    }[],
  ) => void;
};

export default function VerifyEventExpenseMatchesView({
  unmatched,
  onConfirm,
}: VerifyEventExpenseMatchesProps) {
  const { t, i18n } = useTranslation('', { keyPrefix: 'component.modal.uploadFinancialFile' });
  const [matches, setMatches] = useState<EventExpenseMatch[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEventsAndMatch = async () => {
      try {
        setLoading(true);
        
        // Get all events for the community (both past and upcoming)
        const events = await eventService.prependPastEvents();
        
        // Match expenses to events by date
        const matchedTransactions: EventExpenseMatch[] = unmatched.map((transaction) => {
          // Find events that occur on the same date as the transaction
          const matchingEvent = events.find((event: Event) => {
            const transactionDate = new Date(transaction.date);
            const eventDate = new Date(event.startTime);
            const isSameDate = isSameDay(transactionDate, eventDate);
            
            // Only match events that have attending participants
            if (isSameDate) {
              const attendingParticipants = event.participants.filter(
                (participant) => participant.answer === EventParticipationAnswer.YES
              );
              return attendingParticipants.length > 0;
            }
            
            return false;
          });

          return {
            transaction,
            matchedEvent: matchingEvent,
            confirmed: false,
          };
        });

        setMatches(matchedTransactions);
      } catch (error) {
        console.error('Error fetching events and matching expenses:', error);
        // If there's an error, just show unmatched transactions without event matches
        setMatches(unmatched.map((transaction) => ({
          transaction,
          matchedEvent: undefined,
          confirmed: false,
        })));
      } finally {
        setLoading(false);
      }
    };

    fetchEventsAndMatch();
  }, [unmatched]);

  const toggleConfirmation = (index: number) => {
    setMatches((prev) =>
      prev.map((item, i) => 
        i === index ? { ...item, confirmed: !item.confirmed } : item
      ),
    );
  };

  const handleConfirm = () => {
    const confirmedMatches = matches
      .filter((match) => match.confirmed && match.matchedEvent)
      .map(({ transaction, matchedEvent }) => ({
        transaction,
        matchedEvent: matchedEvent!,
      }));

    onConfirm(confirmedMatches);
  };

  const matchesWithEvents = matches.filter((match) => match.matchedEvent);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <div className="mb-2 text-sm text-gray-600">{t('loadingEvents')}</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">
          {t('eventExpenseMatchTitle')}
        </h4>
        <p className="text-sm text-gray-600">
          {t('eventExpenseMatchSubtitle')}
        </p>
      </div>

      <ul className="mb-4 space-y-4">
        {matchesWithEvents.length === 0 && (
          <li className="text-center py-8 text-gray-500">
            {t('noEventMatches')}
          </li>
        )}
        {matchesWithEvents.map((match, index) => {
          const event = match.matchedEvent!;
          const attendingParticipants = event.participants.filter(
            (participant) => participant.answer === EventParticipationAnswer.YES
          );
          
          return (
            <li
              key={`${index}_event_match_${match.transaction.description}`}
              onClick={() => toggleConfirmation(index)}
              className="flex cursor-pointer items-center gap-4 rounded-lg border bg-white p-4 shadow-sm hover:bg-gray-50">
              <input
                type="checkbox"
                checked={match.confirmed}
                onChange={() => toggleConfirmation(index)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-900">
                      {match.transaction.description}
                    </div>
                    <div className="text-sm text-gray-600">
                      {formatEuroCents(match.transaction.amount, i18n.language)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-primary-600">
                      {event.title}
                    </div>
                    <div className="text-xs text-gray-500">
                      {attendingParticipants.length} {t('participants')}
                    </div>
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  {t('eventDate')} {new Date(event.startTime).toLocaleDateString()}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="flex justify-end gap-2">
        <Button 
          variant="primary-dark" 
          type="button" 
          onClick={handleConfirm}>
          {t('continue')}
        </Button>
      </div>
    </div>
  );
}
